import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import '../styles.css';
import { useParams } from 'react-router-dom';
import { SlArrowLeftCircle } from "react-icons/sl";
import api, { stripeBase } from '../config/api';

import belugaImage from '../images/beluga.jpg';
import jasonharbImage from '../images/jasonharb.jpg';

// Admin configuration object - same as in Home.js
const adminConfig = {
  beluga: {
    displayName: "Beluga",
    image: belugaImage,
  },
  jasonharb: {
    displayName: "Jason Harb",
    image: jasonharbImage, // Updated to use Jason's actual profile picture
  }
};

const PaymentModal = ({ open, onClose, clientSecret, creatorConfig, viewerData }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [setupIntentUsed, setSetupIntentUsed] = useState(false);

  // Reset state when modal opens with new client secret
  useEffect(() => {
    if (open && clientSecret) {
      setPaying(false);
      setError("");
      setSuccess(false);
      setSetupIntentUsed(false);
    }
  }, [open, clientSecret]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent duplicate submissions
    if (paying || setupIntentUsed) return;
    
    setPaying(true);
    setError("");
    
    if (!stripe || !elements) {
      setError("Stripe not loaded");
      setPaying(false);
      return;
    }
    
    try {
      console.log("Starting Setup Intent confirmation...");
      console.log("Client secret:", clientSecret ? "Present" : "Missing");
      
      // Check if we have a valid client secret
      if (!clientSecret) {
        setError("Payment session expired. Please try again.");
        return;
      }
      
      // Mark Setup Intent as being used
      setSetupIntentUsed(true);
      
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {
          payment_method_data: {
            billing_details: {
              email: viewerData.email,
              name: viewerData.name,
            },
          },
        },
        redirect: "if_required",
      });
      
      console.log("Setup Intent confirmation result:", result);
      
      if (result.error) {
        console.error("Stripe confirmation error:", result.error);
        
        // Handle specific error types
        if (result.error.code === 'setup_intent_unexpected_state') {
          setError("Payment session expired or invalid. Please refresh and try again.");
        } else {
          setError(`Payment failed: ${result.error.message}`);
        }
      } else if (result.setupIntent && result.setupIntent.status === "succeeded") {
        // Payment successful - now send message to API
        const messagePayload = {
              viewerName: viewerData.name,
              viewerEmail: viewerData.email,
              amount: viewerData.amount,
              message: viewerData.message,
              creatorId: viewerData.creatorId,
          setupIntentId: result.setupIntent.id,
          paymentMethodId: result.setupIntent.payment_method
        };
        console.log("[DEBUG] Sending message payload to API:", messagePayload);
        try {
          const response = await fetch(`${stripeBase}/api/send-message-supabase`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(messagePayload),
          });
          const data = await response.json();
          console.log("[DEBUG] API response:", data);
          if (response.ok && data.success) {
            console.log("Message sent successfully:", data);
            alert("✅ Payment successful! Your message has been sent to the creator.");
            setSuccess(true);
            onClose();
          } else {
            console.error("Failed to send message:", data);
            alert("⚠️ Payment successful, but there was an issue sending your message. Please contact support.");
            setSuccess(true);
            onClose();
          }
        } catch (apiError) {
          console.error("API call failed:", apiError);
          alert("⚠️ Payment successful, but there was an issue sending your message. Please contact support.");
          setSuccess(true);
          onClose();
        }
      } else {
        console.error("Unexpected Setup Intent status:", result.setupIntent?.status);
        setError("Payment setup failed. Please try again.");
      }
    } catch (error) {
      console.error("Payment error:", error);
      setError("Payment failed. Please try again.");
    }
    
    setPaying(false);
  };

  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(0,0,0,0.5)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflowY: "auto", // allow overlay to scroll if needed
        padding: 0,
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          padding: 16,
          minWidth: 320,
          maxWidth: 400,
          minHeight: 500,
          maxHeight: '90vh',
          width: '90vw',
          boxShadow: "0 2px 16px rgba(0,0,0,0.2)",
          position: "relative",
          overflowY: "auto", // allow modal to scroll if needed
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
        }}
      >
        {/* Custom Header Section */}
        <div style={{ 
          marginBottom: 24
        }}>
          {/* Horizontal Top Bar */}
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "space-between",
            marginBottom: 16,
            paddingBottom: 16,
            borderBottom: "1px solid #f0f0f0"
          }}>
            {/* Back Arrow */}
            <button 
              onClick={onClose}
              style={{
                background: "#f5f5f5",
                border: "1px solid #e0e0e0",
                cursor: "pointer",
                padding: 8,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#333",
                fontSize: "18px",
                fontWeight: "bold",
                width: "32px",
                height: "32px",
                transition: "background-color 0.2s ease"
              }}
              onMouseOver={e => (e.target.style.backgroundColor = "#e8e8e8")}
              onMouseOut={e => (e.target.style.backgroundColor = "#f5f5f5")}
            >
              ←
            </button>
            {/* Title */}
            <h2 style={{ 
              margin: 0, 
              fontSize: "20px", 
              fontWeight: "bold", 
              color: "#333",
              flex: 1,
              textAlign: "center"
            }}>
              Save Payment
            </h2>
            {/* Creator Profile Image */}
            {creatorConfig && creatorConfig.image && (
              <div style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                overflow: "hidden",
                flexShrink: 0
              }}>
                <img 
                  src={creatorConfig.image} 
                  alt={creatorConfig.displayName || "Creator"}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover"
                  }}
                />
              </div>
            )}
          </div>
          {/* Subtitle */}
          <p style={{ 
            margin: 0, 
            fontSize: "14px", 
            color: "#666", 
            textAlign: "center",
            lineHeight: "1.4"
          }}>
            No charge now. You pay only if you get a response. Cancel anytime!
          </p>
          {/* Amount Display */}
          <div style={{
            marginTop: 16,
            padding: 16,
            background: "#f8f9fa",
            borderRadius: 8,
            border: "1px solid #e9ecef",
            textAlign: "center"
          }}>
            <div style={{
              fontSize: "12px",
              color: "#6c757d",
              marginBottom: 4,
              fontWeight: "500"
            }}>
              Amount to be charged if you receive a response:
            </div>
            <div style={{
              fontSize: "24px",
              fontWeight: "bold",
              color: "#007BFF"
            }}>
              ${viewerData.amount.toFixed(2)} USD
            </div>
            <div style={{
              fontSize: "11px",
              color: "#6c757d",
              marginTop: 4
            }}>
              Payment will only be processed when the creator responds
            </div>
          </div>
        </div>
        {/* Payment Form */}
        <form onSubmit={handleSubmit} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
          <div style={{ minHeight: 200, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
          <PaymentElement />
          </div>
          <button type="submit" disabled={paying} style={{ marginTop: 16, width: "100%", background: "#007BFF", color: "#fff", border: "none", borderRadius: 8, padding: 12, fontWeight: 600 }}>
            {paying ? "Processing..." : `Confirm Payment Method ($${viewerData.amount.toFixed(2)})`}
          </button>
        </form>
        {error && <div style={{ color: "red", marginTop: 8 }}>{error}</div>}
        <button onClick={onClose} style={{ marginTop: 16, width: "100%", background: "#eee", color: "#333", border: "none", borderRadius: 8, padding: 12 }}>Cancel</button>
      </div>
    </div>
  );
}

const CreatorPage = () => {
  // Initialize Stripe only on client-side
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    // Debug environment variable - log right before Stripe initialization
    console.log('=== STRIPE ENVIRONMENT VARIABLE DEBUG ===');
    console.log('REACT_APP_STRIPE_PUBLISHABLE_KEY value:', process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
    console.log('REACT_APP_STRIPE_PUBLISHABLE_KEY type:', typeof process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
    console.log('REACT_APP_STRIPE_PUBLISHABLE_KEY length:', process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ? process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY.length : 'undefined');
    
    // Log all Stripe-related environment variables
    const stripeEnvVars = Object.keys(process.env).filter(key => key.includes('STRIPE'));
    console.log('All Stripe-related environment variables:', stripeEnvVars);
    stripeEnvVars.forEach(key => {
      const value = process.env[key];
      console.log(`  ${key}:`, value ? `${value.substring(0, 10)}... (${typeof value}, length: ${value.length})` : 'undefined');
    });
    
    // Check for wrong prefix
    if (process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY) {
      console.warn('⚠️ Found NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY but this is a Create React App project!');
      console.warn('⚠️ In Create React App, environment variables must be prefixed with REACT_APP_ not NEXT_PUBLIC_');
      console.warn('⚠️ Please update your Vercel environment variable to use REACT_APP_ prefix');
    }
    
    console.log('==========================================');
    
    if (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
      console.log('✅ Initializing Stripe with publishable key...');
      setStripePromise(loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY));
    } else {
      console.error('❌ REACT_APP_STRIPE_PUBLISHABLE_KEY is not set or undefined');
      console.error('❌ Please check your Vercel environment variables');
      console.error('❌ In Create React App, environment variables must be prefixed with REACT_APP_');
      console.error('❌ Current environment variable should be: REACT_APP_STRIPE_PUBLISHABLE_KEY');
    }
  }, []);

  const [page, setPage] = useState(1);
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [price, setPrice] = useState(10); // Default price value
  const [messageError, setMessageError] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const { admin_name } = useParams();
  const [modalOpen, setModalOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState("");

  // Get the current admin configuration
  const currentAdmin = admin_name || 'beluga';
  const config = adminConfig[currentAdmin] || adminConfig.beluga;


  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const handleNextPage = () => {
    if (page === 1) {
      if (!message) {
        setMessageError(true);
        return;
      }
      setMessageError(false);
      setPage(2);
    } else if (page === 2) {
      let valid = true;
      if (!name) {
        setNameError(true);
        valid = false;
      } else {
        setNameError(false);
      }
      if (!validateEmail(email)) {
        setEmailError(true);
        valid = false;
      } else {
        setEmailError(false);
      }
      if (valid) {
        handleCheckoutRedirect();
      }
    }
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleCheckoutRedirect = async () => {
    setCheckoutError("");
    
    // Prevent multiple requests
    if (modalOpen) return;
    
    try {
      const response = await fetch(`${stripeBase}/api/create-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: Math.round(price * 100),
          message,
          creatorId: admin_name || 'jasonharb',
        }),
      });
      let data;
      try {
        data = await response.json();
      } catch (jsonErr) {
        console.log('Failed to parse JSON:', jsonErr);
        setCheckoutError('Payment server error: Invalid response.');
        alert('Payment server error: Invalid response.');
        return;
      }
      console.log("API response from /api/create-payment-intent:", data);
      if (data && data.client_secret) {
        console.log("Opening modal with client_secret:", data.client_secret);
        setClientSecret(data.client_secret);
        setModalOpen(true);
      } else {
        setCheckoutError('Failed to start payment session. Please try again.');
        alert('Failed to start payment session. Please try again.');
      }
    } catch (error) {
      setCheckoutError('Error connecting to payment server.');
      alert('Error connecting to payment server.');
      console.error(error);
    }
  };

  return (
    <>
    {page != 3 &&
    <div className="title-card">
         <a href={`/${currentAdmin}`} className="title-main">
            ReachMe<sup className="superscript-pro">pro</sup>
        </a>
    </div>
    }
      

      <div className="centered">
      <div className={`container ${page === 2 ? "page-2" : ""}`}>

          {page === 1 ? (
            <>
              {/* Message input form */}
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div className="title" style={{ paddingTop: 7 }}>Message {admin_name || 'Jason Harb'}</div>
              </div>
              <div className="bubble" style={{ marginTop: "5px", marginBottom: "15px", paddingLeft: "15px",  paddingRight: "15px"}}>
                <div className="text">
                  We verify that all creator responses are high quality!
                </div>
              </div>
              <div style={{ position: 'relative', marginBottom: '30px' }}>
                <textarea
                  className={`textarea ${messageError ? 'textarea-error' : ''}`}
                  placeholder="Write anything..."
                  value={message}
                  onChange={(e) => {
                    setMessage(e.target.value);
                    if (e.target.value) setMessageError(false);
                  }}
                  onFocus={() => setMessageError(false)}
                  maxLength={500}
                  required
                />
                {messageError && (
                  <div style={{ position: 'absolute', bottom: '-20px', left: '0', color: '#ff4444', fontSize: '0.8em' }}>
                    Required
                  </div>
                )}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 10,
                    right: 10,
                    fontSize: '0.8em',
                    color: '#666',
                  }}
                >
                  {500 - message.length}
                </div>
              </div>
              <div className="bubble" style={{ marginTop: "5px", marginBottom: "15px" }}>
                <div className="text">
                  <span className="text-clickable">No Charge: </span>
                  Only pay if you receive a response!
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "15px" }}>
                <div style={{ fontSize: "24px", fontWeight: "bold", marginBottom: "10px" }}>${price.toFixed(2)} USD</div>
                <input
                  type="range"
                  min="1"
                  max="100"
                  value={price}
                  onChange={(e) => setPrice(Number(e.target.value))}
                  className="slider"
                  style={{ 
                    width: "80%",
                    background: `linear-gradient(to right, #007BFF 0%, #007BFF ${((price - 1) / (100 - 1)) * 100}%, #d3d3d3 ${((price - 1) / (100 - 1)) * 100}%, #d3d3d3 100%)`
                  }}
                />
              </div>
              <button type="button" className="button" onClick={handleNextPage}>Next</button>
            </>
          ) : page === 2 ? (
            <>
              {/* Name/email input form */}
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button
                  type="button"
                  className="back-button"
                  onClick={handlePreviousPage}
                >
                  Back
                </button>
                <div className="title" style={{ paddingTop: 7 }}>Your details</div>
              </div>
              <div style={{ position: 'relative', marginBottom: '30px' }}>
                <input
                  type="text"
                  className={`form ${nameError ? 'form-error' : ''}`}
                  style={{ marginTop: "5px", marginBottom: "0px" }}
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (e.target.value) setNameError(false);
                  }}
                  onFocus={() => setNameError(false)}
                  required
                />
                {nameError && <div style={{ color: '#ff4444', fontSize: '0.8em', position: 'absolute', bottom: '-20px', left: '3px' }}>Required</div>}
              </div>
              <div style={{ position: 'relative', marginBottom: '20px' }}>
                <input
                  type="email"
                  className={`form ${emailError ? 'form-error' : ''}`}
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (validateEmail(e.target.value)) setEmailError(false);
                  }}
                  onFocus={() => setEmailError(false)}
                  required
                />
                {emailError && <div style={{ color: '#ff4444', fontSize: '0.8em', position: 'absolute', bottom: '-20px', left: '3px' }}>Enter Valid Email</div>}
              </div>
              <div style={{ display: "flex", textAlign: "left" }}>
                <div className="text-bold" style={{ marginTop: "10px", paddingLeft: "3px" }}>
                  You will get an email confirmation for your order and notification when the creator replies.
                </div>
              </div>
              
              {/* Price Reminder */}
              <div style={{
                marginTop: 20,
                padding: 12,
                background: "#f8f9fa",
                borderRadius: 8,
                border: "1px solid #e9ecef",
                textAlign: "center"
              }}>
                <div style={{
                  fontSize: "12px",
                  color: "#6c757d",
                  marginBottom: 4
                }}>
                  Amount to be charged if you receive a response:
                </div>
                <div style={{
                  fontSize: "20px",
                  fontWeight: "bold",
                  color: "#007BFF"
                }}>
                  ${price.toFixed(2)} USD
                </div>
              </div>
              
              <button type="button" className="button" style={{ marginTop: "20px", color: "white" }} onClick={handleNextPage}>
                Checkout (${price.toFixed(2)})
              </button>
              {checkoutError && (
                <div style={{ color: 'red', marginTop: '10px', textAlign: 'center' }}>{checkoutError}</div>
              )}
            </>
          ) : null}
        </div>
      </div>

      {/* Inline CSS for the slider */}
      <style>
        {`

        .button {
  margin-top: 25px; /* Default margin-top */
  color: #fff;
  background: ""; /* Add the appropriate background color if needed */
}
   .payment-note {
            text-align: center;
            color: #666;
            font-size: .9rem;
            line-height: 1;
            margin-left: 50px;
            margin-right: 50px;
            margin-top: -20px;
            margin-bottom: -20px;
        }

@media (max-width: 787px) {
  .button {
    margin-top: 10px; /* Smaller margin-top for screens narrower than 787px */
  }
}
  




             @keyframes glowing {
          0% { box-shadow: 0 0 5px #007BFF; }
          50% { box-shadow: 0 0 20px #007BFF; }
          100% { box-shadow: 0 0 5px #007BFF; }
        }
          .text-clickable{
          font-size: 1.23rem;
          color:#007BFF;
          }
          .text{
          font-size: 1rem;
          }
          
        
        .glow-container {
          position: relative;
          margin-top: -150px;
          display: flex;
          justify-content: center;
          width: 100%;
          z-index: 0;
          position: absolute; /* Centering */
          top: 70%; /* Centering */
          left: 50%; /* Centering */
          transform: translate(-50%, -50%); /* Centering */
        }

        .glow {
          width: 450px;
          height: 350px;
          border-radius: 50%;
          filter: blur(90px); /* Increased blur for a more subtle effect */
          margin: 0 -0px; /* Use negative margin to overlap the glows */
        }

        .pink-glow {
          background: radial-gradient(circle, rgba(255,0,255,1) 0%, rgba(255,0,255,0.2) 50%, rgba(255,0,255,0) 100%);
        }

        .blue-glow {
          background: radial-gradient(circle, rgba(0, 123, 255, 1) 0%, rgba(0, 123, 255, 0.2) 50%, rgba(0, 123, 255, 0) 100%);
        }

          .slider {
            -webkit-appearance: none;
            width: 100%;
            height: 8px;
            border-radius: 5px;
            outline: none;
            opacity: 0.7;
            -webkit-transition: .2s;
            transition: opacity .2s;
          }

          .slider:hover {
            opacity: 1;
          }

          .slider::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 25px;
            height: 25px;
            border-radius: 50%;
            background: #007BFF;
            cursor: pointer;
          }

          .slider::-moz-range-thumb {
            width: 25px;
            height: 25px;
            border-radius: 50%;
            background: #007BFF;
            cursor: pointer;
          }

          .elements-wrapper {
            width: 100%;
            max-height: 600px;
            margin: 0 auto;
          }

          .payment-form {
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
          }

          .payment-form .StripeElement {
            width: 100%;
            margin-bottom: 10px;
          }

          .payment-form button {
            width: 100%;
            max-width: 200px;
          }

          .title-card {
                position: absolute;
                top: 20px;
                left: 20px;
                z-index: 10;

            }

            .title, .title-main{
                font-family: 'Jockey+One', Jockey+One;
                font-size: 2rem;
                font-weight: bold;
                color: #fff;
                    text-decoration: none; /* Remove underline */

            }

            .superscript-pro {
                font-size: 0.4em;
                vertical-align: super;
                color: #007BFF;
            }


             @media only screen and (max-width: 600px) {
             .title-main {
                font-family: 'Jockey+One', Jockey+One;
                font-size: 1.5rem;
                font-weight: bold;
                color: #fff;
                    text-decoration: none; /* Remove underline */

            }
              // .back-button{
              //     font-family: SFProMedium;
              //     color: var(--background);
              //     font-size: 10px;
              //     padding: 5px;
              //     padding-left: 9px;
              //     padding-right: 10px;
              //     border-radius: 50px;
              //     height: 35px;
              //     width: 12%;
              //     text-decoration: none !important;
              //     opacity: 1;
    
              // }
        }

        `}
      </style>
      {clientSecret && stripePromise && (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <PaymentModal 
            open={modalOpen} 
            onClose={() => {
              setModalOpen(false);
              setClientSecret(""); // Clear client secret to prevent reuse
            }} 
            clientSecret={clientSecret} 
            creatorConfig={config}
            viewerData={{
              name: name,
              email: email,
              amount: price,
              message: message,
              creatorId: admin_name || 'jasonharb'
            }}
          />
        </Elements>
      )}
    </>
  );
};

export default CreatorPage;
